import React, { useEffect, useRef, useState } from 'react'
import styles from '../TimesheetsPage.module.css'
import o from '../../components/Overlay.module.css'
import ov from './overlays.module.css'
import { apiJson, ApiError } from '../../lib/api'
import { hm, typeHm } from './time'
import type { EntryZone, Person, Team } from './types'
import { useChanged, useLayer } from '../../components/layers'
import { PendingButton, useSlow, inertClass } from '../../components/pending'
import { DateField } from '../../components/DateField'

// Add worker shift (CONTROL V4 openTsAdd / confirmTsAdd → POST /api/timesheets).
// The server writes the shift, its first session and a "Worker shift added"
// audit event in one transaction; a second shift for the same worker and date
// is refused (SHIFT_EXISTS) with the id of the one to open instead.

interface Props {
  user: any
  date: string
  people: Person[]
  teams: Team[]
  onClose: () => void
  onAdded: (flash: string, shiftId: string, date: string) => void
  onOpenExisting: (shiftId: string, date: string) => void
}

export const personName = (p: Person) => `${p.preferred_name || p.first_name} ${p.last_name}`.trim()

export function AddShiftModal({ user, date, people, teams, onClose, onAdded, onOpenExisting }: Props) {
  const [a, setA] = useState({ worker: '', date, team: teams[0]?.id || '', start: '07:00', finish: '15:30' })
  const [error, setError] = useState<string | null>(null)
  const [existing, setExisting] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  // D6.13: the zone these local times will be read in, shown beside them. The
  // work site is optional; with none chosen the business zone applies.
  const [siteId, setSiteId] = useState('')
  const [zone, setZone] = useState<EntryZone | null>(null)
  const [zoneFailed, setZoneFailed] = useState(false)
  // A repeated clock time (the autumn change): the server asks, the person says.
  const [asked, setAsked] = useState<{ which: 'start' | 'finish'; error: string; options: { occurrence: 'earlier' | 'later'; label: string }[] } | null>(null)
  // D6.13: a site whose UTC was written automatically is not a proven zone.
  // The person confirms the zone before a time is read in it; it is recorded.
  const [zoneOk, setZoneOk] = useState(false)
  const [zoneAsked, setZoneAsked] = useState<string | null>(null)
  const [occ, setOcc] = useState<{ start?: 'earlier' | 'later'; finish?: 'earlier' | 'later' }>({})
  useEffect(() => {
    let cancelled = false
    setZoneFailed(false)
    setZoneOk(false)
    setZoneAsked(null)
    apiJson<EntryZone>(user, `/api/timesheets/zone${siteId ? `?site_id=${encodeURIComponent(siteId)}` : ''}`)
      .then((z) => !cancelled && setZone(z))
      .catch(() => !cancelled && setZoneFailed(true))
    return () => {
      cancelled = true
    }
  }, [user, siteId])
  const set = (k: keyof typeof a, v: string) => {
    setError(null)
    setExisting(null)
    if (k === 'start' || k === 'finish' || k === 'date') {
      setAsked(null)
      setOcc({})
    }
    setA((p) => ({ ...p, [k]: k === 'start' || k === 'finish' ? typeHm(v) : v }))
  }
  // h 1753-1756: a finish at or before the start is either a typing mistake or
  // the next calendar day, and only the person entering it knows which.
  const [nextDay, setNextDay] = useState(false)
  const s = hm(a.start)
  const f = hm(a.finish)
  const wraps = s !== null && f !== null && f <= s
  // No zone to read the times in: say what to configure; nothing can be saved.
  const noZone = !!zone && !zone.zone
  const needsZoneConfirm = !!zone?.zone && ((zone.needs_confirmation || []).length > 0 || !!zoneAsked)
  // Teams can arrive after the modal mounts; the select shows the first one, so
  // the value sent must be the first one too, not the empty initial state.
  const team = a.team || teams[0]?.id || ''
  const blocked = !a.worker || !team || s === null || (wraps && !nextDay) || noZone || (needsZoneConfirm && !zoneOk)
  const slow = useSlow(busy)
  // A layer (h 425-458): Escape, the scrim and Cancel ask first once a value
  // has changed. "Open that shift" is not a discard: the server has just said
  // this shift already exists, so it goes straight there. Nothing closes it
  // while its write is out (h 3984-3992).
  const panelEl = useRef<HTMLDivElement>(null)
  const dirty = useChanged({ a, nextDay })
  const { requestClose, confirm: discardQ } = useLayer(true, onClose, { dirty: dirty && !busy, el: panelEl, modal: true, focusFirst: true, busy })

  async function confirm() {
    if (blocked) return
    setBusy(true)
    try {
      const r = await apiJson<{ id: string; flash: string; note?: string }>(user, '/api/timesheets', {
        body: {
          human_id: a.worker,
          work_date: a.date,
          team_id: team,
          start_time: a.start,
          finish_time: a.finish.trim() ? a.finish : null,
          finish_next_day: nextDay && f !== null,
          site_id: siteId || undefined,
          start_occurrence: occ.start,
          finish_occurrence: occ.finish,
          confirm_zone: needsZoneConfirm && zoneOk ? true : undefined,
        },
      })
      onAdded([r.flash, r.note].filter(Boolean).join(' · '), r.id, a.date)
    } catch (e: any) {
      if (e instanceof ApiError && e.body?.code === 'SHIFT_EXISTS') setExisting(e.body.shift_id)
      if (e instanceof ApiError && e.body?.code === 'AMBIGUOUS_LOCAL_TIME') {
        setAsked({ which: e.body.which, error: e.message, options: e.body.options || [] })
        setBusy(false)
        return
      }
      if (e instanceof ApiError && e.body?.code === 'ZONE_CONFIRMATION_REQUIRED') {
        setZoneAsked(e.message)
        setZoneOk(false)
        setBusy(false)
        return
      }
      setError(e.message || 'Could not add this shift')
      setBusy(false)
    }
  }

  return (
    <>
    <div className={o.scrim} onClick={(e) => e.target === e.currentTarget && requestClose()}>
      <div ref={panelEl} tabIndex={-1} className={`${o.modal} ${ov.addModal}`} role="dialog" aria-label="Add worker shift">
        <div className={o.head}>
          <div className={o.headText}>
            <h2 className={o.title}>Add worker shift</h2>
            <p className={o.sub}>A shift added by hand is audited like any other adjustment. Sessions can be added to it afterwards.</p>
          </div>
        </div>
        <div className={`${o.body} ${inertClass(busy, slow)}`}>
          <div className={o.field}>
            <label className={o.fieldLabel} htmlFor="add-worker">Worker</label>
            <select id="add-worker" className={o.input} value={a.worker} onChange={(e) => set('worker', e.target.value)}>
              <option value="" />
              {people.map((p) => (
                <option key={p.id} value={p.id}>
                  {personName(p)}
                </option>
              ))}
            </select>
          </div>
          <div className={o.grid2}>
            <div className={o.field}>
              <label className={o.fieldLabel} htmlFor="add-date">Work date</label>
              <DateField id="add-date" className={o.input} value={a.date} onChange={(e) => set('date', e.target.value)} />
            </div>
            <div className={o.field}>
              <label className={o.fieldLabel} htmlFor="add-team">Initial team</label>
              <select id="add-team" className={o.input} value={team} onChange={(e) => set('team', e.target.value)}>
                {teams.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
            </div>
            <div className={o.field}>
              <label className={o.fieldLabel} htmlFor="add-start">Start</label>
              <input id="add-start" className={o.input} value={a.start} placeholder="07:00" onChange={(e) => set('start', e.target.value)} />
            </div>
            <div className={o.field}>
              <label className={o.fieldLabel} htmlFor="add-finish">Finish: leave blank if still working</label>
              <input id="add-finish" className={o.input} value={a.finish} placeholder="15:30" onChange={(e) => set('finish', e.target.value)} />
            </div>
          </div>
          <div className={o.field}>
            <label className={o.fieldLabel} htmlFor="add-site">Work site (optional)</label>
            <select id="add-site" className={o.input} value={siteId} onChange={(e) => { setSiteId(e.target.value); setAsked(null); setOcc({}); setError(null) }}>
              <option value="">No site: use the business zone</option>
              {(zone?.sites || []).map((x) => (
                <option key={x.id} value={x.id}>
                  {x.name}
                </option>
              ))}
            </select>
            <div className={o.fieldHint} data-testid="entry-zone">
              {zoneFailed
                ? 'The time zone could not be loaded. These times are read in a configured zone, never your browser’s.'
                : !zone
                  ? 'Checking the time zone…'
                  : zone.zone
                    ? `These times are read in ${zone.zone} (${zone.source_label}${zone.fell_back_from_site ? '; this site has no zone of its own' : ''}).`
                    : zone.problem?.message}
            </div>
          </div>
          {needsZoneConfirm && (
            <div className={o.warn} role="alert" data-testid="zone-confirmation">
              <div className={ov.stack}>
                <span>{zoneAsked || 'This site’s time zone is UTC, which was written automatically when zones were introduced; it has not been chosen by anyone.'}</span>
                <label className={ov.check}>
                  <input type="checkbox" checked={zoneOk} aria-label="Confirm the time zone" onChange={(e) => setZoneOk(e.target.checked)} />
                  I confirm these times were worked in {zone?.zone}. This is recorded on the shift.
                </label>
              </div>
            </div>
          )}
          {(wraps || nextDay) && (
            <div className={ov.stack}>
              <label className={ov.check}>
                <input
                  type="checkbox"
                  checked={nextDay}
                  onChange={(e) => {
                    setError(null)
                    setNextDay(e.target.checked)
                  }}
                />
                Finishes the next day. The shift stays on the work date above.
              </label>
              {wraps && !nextDay && <div className={o.warn}>Finish is not after start. Tick this if the shift ran past midnight.</div>}
            </div>
          )}
          {asked && (
            <div className={o.danger} role="alert" data-testid="occurrence-question">
              <div className={ov.stack}>
                <span>{asked.error}</span>
                {asked.options.map((op) => (
                  <label key={op.occurrence} className={ov.check}>
                    <input
                      type="radio"
                      name="occurrence"
                      aria-label={op.label}
                      checked={occ[asked.which] === op.occurrence}
                      onChange={() => setOcc((p) => ({ ...p, [asked.which]: op.occurrence }))}
                    />
                    {op.label}
                  </label>
                ))}
              </div>
            </div>
          )}
          {error && (
            <div className={o.danger} role="alert">
              <span>
                {error}
                {existing && (
                  <>
                    {' '}
                    <button className={styles.linkBtn} onClick={() => onOpenExisting(existing, a.date)}>
                      Open that shift
                    </button>
                  </>
                )}
              </span>
            </div>
          )}
        </div>
        <div className={o.foot}>
          <button className={o.ghost} onClick={requestClose} disabled={busy}>
            Cancel
          </button>
          <PendingButton className={o.primary} onClick={confirm} disabled={blocked || (!!asked && !occ[asked.which])} busy={busy} pending="Adding…">
            Add shift
          </PendingButton>
        </div>
      </div>
    </div>
    {discardQ}
    </>
  )
}
